/**
 * Enregistrement du service worker (production) et démarrage de la synchronisation hors ligne.
 */
import { axiosInstance } from '@/lib/api';
import { startOfflineSync } from './offline-api';
import { refreshAppShellIfNeeded } from './offline-downloads';

/**
 * Le cache /assets du service worker garde les fichiers de chaque déploiement visité : au-delà de 400 fichiers,
 * il est vidé (les fichiers nécessaires hors ligne restent dans la coquille courante).
 */
async function trimRuntimeAssetsCache(): Promise<void> {
  try {
    if (!('caches' in window) || navigator.onLine === false) return;
    const cache = await caches.open('lyrra-assets-v1');
    const keys = await cache.keys();
    if (keys.length > 400) await caches.delete('lyrra-assets-v1');
  } catch {
    /* cache indisponible */
  }
}

export function initOfflineMode(currentLanguage: () => string): void {
  if (typeof window === 'undefined') return;
  startOfflineSync(axiosInstance);
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  const register = () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then(async (registration) => {
        // Background Sync (Chrome / Android) : le service worker réveille la synchronisation au retour du réseau.
        try {
          const sync = (registration as ServiceWorkerRegistration & { sync?: { register: (tag: string) => Promise<void> } }).sync;
          await sync?.register('lyrra-outbox');
        } catch {
          /* non pris en charge : la page synchronise elle-même (événement « online ») */
        }
        await navigator.serviceWorker.ready;
        void trimRuntimeAssetsCache();
        void refreshAppShellIfNeeded(currentLanguage());
      })
      .catch((error) => {
        console.warn('[offline] service worker non enregistré', error);
      });
  };
  if (document.readyState === 'complete') register();
  else window.addEventListener('load', register, { once: true });
}
